import { useEffect, useMemo, useRef, useState } from "react";
import {
  useAssignMemberToOfficeMutation,
  useGetExecutiveOfficesQuery,
} from "../api/executiveOfficeApi";
import { useLazySearchMembersByQQuery } from "../../application/api/probationerApplicationApi";

import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import {
  UserPlus,
  Search,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ShieldAlert,
} from "lucide-react";

// search() returns other_names (= firstname) and member_id (UUID)
function fullName(m: any) {
  return [m?.surname, m?.other_names ?? m?.firstname, m?.othernames]
    .filter(Boolean)
    .join(" ");
}

export default function AssignExecutiveToSet({
  executiveSetId,
  existingMemberIds,
  onSuccess,
}: {
  executiveSetId: number;
  existingMemberIds: string[];
  onSuccess: () => void;
}) {
  const { data: offices = [] } = useGetExecutiveOfficesQuery();

  // Same lazy + debounce pattern as ManageExaminersPage (chief examiners)
  const [query, setQuery] = useState("");
  const [triggerSearch, searchState] = useLazySearchMembersByQQuery();
  const members = useMemo(
    () => ((searchState.data as any)?.data || []) as any[],
    [searchState.data],
  );

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (query.trim().length < 2) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => triggerSearch(query.trim()), 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, triggerSearch]);

  const [assign, { isLoading: assigning }] = useAssignMemberToOfficeMutation();

  const [form, setForm] = useState({
    member_id: "",
    office_id: 0,
    position: "",
  });

  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const alreadyAssigned = useMemo(() => {
    return form.member_id ? existingMemberIds.includes(form.member_id) : false;
  }, [form.member_id, existingMemberIds]);

  const submit = async () => {
    setMsg(null);
    if (!form.member_id)
      return setMsg({ type: "error", text: "Pick a member first." });
    if (!form.office_id)
      return setMsg({ type: "error", text: "Select an office." });

    if (alreadyAssigned) {
      return setMsg({
        type: "error",
        text: "This member is already assigned in this set.",
      });
    }

    try {
      const res: any = await assign({
        member_id: form.member_id,
        office_id: form.office_id,
        executive_set_id: executiveSetId,
        position: form.position || undefined,
      }).unwrap();

      // Assigning into a term that isn't the sitting one records the service
      // without granting any access; the server's message says so.
      setMsg({ type: "success", text: res?.message || "Assigned successfully." });
      setForm({ member_id: "", office_id: 0, position: "" });
      setQuery("");
      onSuccess();
    } catch (e: any) {
      setMsg({
        type: "error",
        text: e?.data?.message || e?.error || "Assign failed",
      });
    }
  };

  return (
    <AccessGate
      perm={PERMS.executive_office.assign}
      fallback={
        <div className="border rounded-xl p-6 mt-6 bg-gray-50 border-dashed flex flex-col items-center">
          <ShieldAlert className="w-8 h-8 text-gray-300 mb-2" />
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest text-center">
            Administrative Access Required to Assign Executives
          </p>
        </div>
      }
    >
      <div className="bg-white border rounded-2xl p-6 mt-6 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="p-2 bg-gray-900 rounded-lg">
            <UserPlus className="w-4 h-4 text-white" />
          </div>
          <h2 className="font-black text-gray-900 uppercase tracking-tight">
            Assign Executive
          </h2>
        </div>

        {/* Feedback Messages */}
        {msg && (
          <div
            className={`mb-4 flex items-center gap-3 rounded-xl border p-4 text-sm font-bold animate-in fade-in slide-in-from-top-2 ${
              msg.type === "success"
                ? "bg-green-50 border-green-100 text-green-800"
                : "bg-red-50 border-red-100 text-red-800"
            }`}
          >
            {msg.type === "success" ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <AlertCircle className="w-4 h-4" />
            )}
            {msg.text}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Member Search Column */}
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block">
                1. Search Member
              </label>
              <div className="relative">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full border-2 border-gray-100 rounded-xl px-4 py-3 text-sm font-medium focus:border-gray-900 transition-all outline-none pl-10"
                  placeholder="Name, membership no, email…"
                  disabled={assigning}
                />
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                {searchState.isFetching && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 absolute right-3.5 top-1/2 -translate-y-1/2" />
                )}
              </div>

              {/* Search Results Dropdown */}
              {query.trim().length >= 2 && !searchState.isFetching && (
                <div className="border-2 border-gray-100 rounded-xl mt-2 max-h-56 overflow-y-auto shadow-xl bg-white animate-in zoom-in-95 duration-150 z-10 relative">
                  {members.length ? (
                    <div className="divide-y divide-gray-50">
                      {members.map((m: any) => {
                        // search() returns member_id (UUID) — use it for assignment
                        const memberId = m.member_id ?? m.id;
                        const picked = form.member_id === memberId;
                        const dup = existingMemberIds.includes(memberId);

                        return (
                          <button
                            key={memberId}
                            type="button"
                            onClick={() =>
                              setForm((f) => ({ ...f, member_id: memberId }))
                            }
                            className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                              picked ? "bg-gray-900 text-white" : ""
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div
                                className={`font-black text-xs uppercase tracking-tight ${
                                  picked ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {fullName(m)}
                              </div>
                              {dup && (
                                <span className="text-[8px] font-black px-2 py-0.5 rounded-full bg-red-100 text-red-700 uppercase">
                                  In Set
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] font-bold text-gray-400">
                              {m.membership_no} • {m.email}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-[10px] font-bold text-gray-400 uppercase">
                      No members found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Office & Position Column */}
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block">
                2. Select Office
              </label>
              <select
                value={form.office_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, office_id: Number(e.target.value) }))
                }
                className="w-full border-2 border-gray-100 rounded-xl px-4 py-3 text-sm font-bold focus:border-gray-900 transition-all outline-none bg-white"
                disabled={assigning}
              >
                <option value={0}>Select Executive Office</option>
                {offices.map((o: any) => (
                  <option key={o.id} value={o.id}>
                    {o.name.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block">
                3. Position (Optional)
              </label>
              <input
                value={form.position}
                onChange={(e) =>
                  setForm((f) => ({ ...f, position: e.target.value }))
                }
                className="w-full border-2 border-gray-100 rounded-xl px-4 py-3 text-sm font-medium focus:border-gray-900 transition-all outline-none"
                placeholder="e.g. Acting Chairman"
                disabled={assigning}
              />
            </div>
          </div>
        </div>

        {/* Action Footer */}
        <div className="mt-8 pt-6 border-t flex items-center justify-between">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            {form.member_id ? "Member selected ✓" : "Please select a member"}
          </div>

          <button
            onClick={submit}
            disabled={
              assigning || !form.member_id || !form.office_id || alreadyAssigned
            }
            className="px-8 py-3.5 rounded-xl bg-gray-900 text-white font-black text-[10px] uppercase tracking-widest shadow-xl shadow-gray-900/20 disabled:opacity-50 transition-all active:scale-95 flex items-center gap-2"
          >
            {assigning ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Assigning...
              </>
            ) : (
              "Confirm Assignment"
            )}
          </button>
        </div>
      </div>
    </AccessGate>
  );
}
